import React, { ReactNode, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, AvatarGroup, Box, IconButton, Slide, SlideProps, Snackbar } from '@mui/material';

import './customNotification.scss';

import ArrowRight from '@/components/Icons/icons/ArrowRight';

interface CustomNotificationProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string | ReactNode;
  avatars: string[];
  actionText?: string;
  icon: React.ReactNode;
  onActionClick?: () => void;
  autoHideDuration?: number; // 3 seconds
  instantClose?: boolean;
  playSound?: boolean;
}

// Custom slide transition
const SlideTransition = React.forwardRef<HTMLDivElement, SlideProps>((props, ref) => {
  return <Slide {...props} direction="left" ref={ref} />;
});
SlideTransition.displayName = 'SlideTransition';

const CustomNotification: React.FC<CustomNotificationProps> = ({
  open,
  onClose,
  title = 'New Notification',
  message,
  avatars,
  actionText = 'Check it out',
  icon,
  onActionClick,
  autoHideDuration = 3000, // 3 seconds
  instantClose = false,
  playSound = false,
}) => {
  const playNotificationSound = () => {
    const audio = document.getElementById('notification-sound') as HTMLAudioElement;
    if (audio) {
      audio.volume = 0.2;
      audio.play();
    }
  };

  useEffect(() => {
    if (open && playSound) {
      playNotificationSound();
    }
  }, [open, playSound]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    onClose();
  };

  const handleActionClick = () => {
    if (onActionClick) onActionClick();
    else onClose();
  };

  const renderAvatars = () => {
    if (!avatars) return null;

    // If there's only one avatar, render it separately
    if (avatars.length === 1) {
      return <Avatar src={avatars[0]} className="custom-notification__single-avatar" />;
    }

    // Render exactly two avatars without count indicator
    return (
      <div className="custom-notification__avatar-group">
        {avatars.slice(0, 2).map((avatarUrl, index) => (
          <Avatar key={index} src={avatarUrl} className="custom-notification__avatar" />
        ))}
      </div>
    );
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={SlideTransition}
      TransitionProps={{
        timeout: {
          enter: 600,
          exit: instantClose ? 0 : 400,
        },
      }}
      autoHideDuration={autoHideDuration}
      className="custom-notification__snackbar">
      <Box className="custom-notification__container">
        <Box className="custom-notification__header">
          <Box className="custom-notification__title">
            {icon}
            <div className="font-medium">{title}</div>
          </Box>
          <IconButton size="small" color="inherit" onClick={handleClose} className="custom-notification__close-button">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className="custom-notification__content">
          {renderAvatars()}
          <div className="custom-notification__message">{message}</div>
        </Box>

        <Box onClick={handleActionClick} className="custom-notification__action">
          <div className="custom-notification__action-text">{actionText}</div>
          <ArrowRight color="white" />
        </Box>
      </Box>
    </Snackbar>
  );
};

export default CustomNotification;
